import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Dropdown, Menu, Button, Modal } from 'antd';
import { DownOutlined, SearchOutlined, CaretUpOutlined, CaretDownOutlined, FileExcelOutlined } from '@ant-design/icons';
import NavbarDropshipping from '../navbarDropshipping/navbarDropshipping';
import './sucursalPedidos.css';
import { PDFDocument } from 'pdf-lib';
import * as XLSX from 'xlsx'; // Add this import for Excel support

const ProductTablePedidos = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    estadoOrden: '',
    transportadora: '',
    estadoEnvio: '',
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [showTransportadoraDropdown, setShowTransportadoraDropdown] = useState(false);
  const [showEstadoEnvioDropdown, setShowEstadoEnvioDropdown] = useState(false);
  const transportadoraRef = useRef(null);
  const estadoEnvioRef = useRef(null);
  const usuario_id = localStorage.getItem('usuario_id');
  
  // Estados para la tabla personalizada
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'fecha_estado', direction: 'descending' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`https://api.99envios.app/api/listar_ordenes/${usuario_id}`);
        const filteredData = response.data.filter(item => item.origen_creacion === 2);
        const formattedData = filteredData.map((item, index) => ({
          key: item.id?.toString() || index.toString(), // Usar ID real o index como fallback
          id: item.id || index + 1, // Usar ID real de la base de datos
          id_variacion: item.id_variacion || '',
          talla: item.talla || '',
          color: item.color || '',
          nombre_producto: item.nombre_producto,
          fecha_estado: item.fecha_estado,
          orderDateObj: new Date(item.fecha_estado), // Para ordenamiento
          estado_envio: item.estado_envio,
          transportadora: item.transportadora,
          valor_proveedor: item.valor_proveedor || 0,
          valor_total: item.valor_venta || 0,
          valor_utilidad: item.valor_utilidad || 0,
          numero_guia: item.preeenvio?.numero_preenvio || '',
          pdf: item.pdf || '',
          nombre_destinatario: item.preeenvio?.destinatario ? 
            `${item.preeenvio.destinatario.nombre || ''} ${item.preeenvio.destinatario.primer_apellido || ''}` : 
            'Sin destinatario',
          telefono_destinatario: item.preeenvio?.destinatario?.telefono || '',
          direccion_destinatario: item.preeenvio?.destinatario?.direccion || '',
          selected: false,
        }))
        .sort((a, b) => {
          const dateA = new Date(a.fecha_estado);
          const dateB = new Date(b.fecha_estado);
          return dateB - dateA; // Descendente: más reciente primero
        });
        console.log('Datos ordenados (Sucursal):', formattedData.slice(0, 5).map(item => ({ 
          id: item.id, 
          date: item.fecha_estado,
          formatted: new Date(item.fecha_estado).toLocaleDateString('es-CO') 
        })));
        setData(formattedData);
        setFilteredData(formattedData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [usuario_id]);

  const handleCheckboxChange = (key) => {
    const updatedData = data.map(item => 
      item.key === key ? { ...item, selected: !item.selected } : item
    );
    setData(updatedData);
    setFilteredData(updatedData);
    setSelectedItems(updatedData.filter(item => item.selected).map(item => item.key));
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters({ ...filters, [filterName]: value });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (transportadoraRef.current && !transportadoraRef.current.contains(event.target)) {
        setShowTransportadoraDropdown(false);
      }
      if (estadoEnvioRef.current && !estadoEnvioRef.current.contains(event.target)) {
        setShowEstadoEnvioDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let result = data;
    if (searchText) {
      result = result.filter(
        (item) =>
          item.nombre_producto.toLowerCase().includes(searchText.toLowerCase()) ||
          item.numero_guia.toLowerCase().includes(searchText.toLowerCase()) ||
          item.nombre_destinatario.toLowerCase().includes(searchText.toLowerCase()) ||
          item.telefono_destinatario.includes(searchText)
      );
    }
    if (filters.estadoOrden) {
      result = result.filter((item) => item.estado_orden === parseInt(filters.estadoOrden));
    }
    if (filters.transportadora) {
      result = result.filter((item) => item.transportadora.toLowerCase() === filters.transportadora.toLowerCase());
    }
    if (filters.estadoEnvio) {
      result = result.filter((item) => item.estado_envio.toLowerCase() === filters.estadoEnvio.toLowerCase());
    }
    
    // Mantener el ordenamiento por fecha después de filtrar
    result = result.sort((a, b) => {
      const dateA = new Date(a.fecha_estado);
      const dateB = new Date(b.fecha_estado);
      return dateB - dateA; // Descendente: más reciente primero
    });
    
    setFilteredData(result);
    setCurrentPage(1); // Volver a la primera página cuando cambian los filtros
  }, [searchText, filters, data]);

  // Función para ordenar datos
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Datos ordenados y paginados
  const sortedData = useMemo(() => {
    let sortableItems = [...filteredData];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        // Manejar ordenamiento para fechas
        if (sortConfig.key === 'fecha_estado') {
          const dateA = new Date(a.fecha_estado);
          const dateB = new Date(b.fecha_estado);
          if (sortConfig.direction === 'ascending') {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        }

        // Manejar ordenamiento para números
        if (['valor_proveedor', 'valor_total', 'valor_utilidad'].includes(sortConfig.key)) {
          if (sortConfig.direction === 'ascending') {
            return a[sortConfig.key] - b[sortConfig.key];
          } else {
            return b[sortConfig.key] - a[sortConfig.key];
          }
        }

        // Ordenamiento para strings
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredData, sortConfig]);

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Función para renderizar el encabezado de la columna con indicador de orden
  const renderColumnHeader = (key, title) => {
    const isActive = sortConfig.key === key;
    return (
      <div 
        className="table-header-cell"
        onClick={() => requestSort(key)}
      >
        {title}
        {isActive && (
          <span className="sort-indicator">
            {sortConfig.direction === 'ascending' ? <CaretUpOutlined /> : <CaretDownOutlined />}
          </span>
        )}
      </div>
    );
  };

  // Función para renderizar el contenido de la variación
  const renderVariacion = (item) => {
    // Check if all three values are missing
    if (!item.id_variacion && !item.talla && !item.color) {
      return <div>N/A</div>;
    }
    
    // Otherwise show each value individually
    const idVariacion = item.id_variacion ? `ID: ${item.id_variacion}` : 'N/A';
    const talla = item.talla ? `Talla: ${item.talla}` : 'N/A';
    const color = item.color ? `Color: ${item.color}` : 'N/A';

    return (
      <div>
        <p>{idVariacion}</p>
        <p>{talla}</p>
        <p>{color}</p>
      </div>
    );
  };

  // Download combined PDF function
  const downloadCombinedPDF = async () => {
    const selectedData = data.filter(item => selectedItems.includes(item.key));
    
    if (selectedData.length === 0) {
      Modal.warning({
        title: 'Selección requerida',
        content: 'Por favor seleccione al menos un pedido para descargar.'
      });
      return;
    }
    
    // Filter items with PDF URLs
    const itemsWithPdf = selectedData.filter(item => item.pdf);
    const itemsWithoutPdf = selectedData.filter(item => !item.pdf);
    
    if (itemsWithPdf.length === 0) {
      Modal.warning({
        title: 'Sin PDFs',
        content: 'Ninguno de los pedidos seleccionados tiene PDF disponible.'
      });
      return;
    }
    
    // Notify about items without PDF
    if (itemsWithoutPdf.length > 0) {
      const itemIds = itemsWithoutPdf.map(item => item.numero_guia || 'Sin número').join(', ');
      Modal.warning({
        title: 'PDFs no disponibles',
        content: `Los siguientes pedidos no tienen PDF disponible: ${itemIds}`
      });
    }
    
    try {
      // Create a new PDF document
      const mergedPdf = await PDFDocument.create();
      
      // Fetch and combine all PDFs
      for (const item of itemsWithPdf) {
        try {
          const pdfResponse = await fetch(item.pdf);
          const pdfBytes = await pdfResponse.arrayBuffer();
          
          // Load the PDF
          const pdf = await PDFDocument.load(pdfBytes);
          
          // Get all pages from the current PDF
          const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
          
          // Add all pages to the merged PDF
          pages.forEach(page => mergedPdf.addPage(page));
        } catch (error) {
          console.error(`Error processing PDF for item ${item.id}:`, error);
        }
      }
      
      // Save the merged PDF
      const mergedPdfBytes = await mergedPdf.save();
      
      // Create a blob with the PDF data
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      
      // Create a URL for the blob
      const url = URL.createObjectURL(blob);
      
      // Create a link to download the PDF
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedidos_combinados_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      
      // Trigger the download
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error combining PDFs:', error);
      Modal.error({
        title: 'Error',
        content: 'Error al combinar los PDFs. Por favor intente de nuevo.'
      });
    }
  };

  // Function to export selected data to Excel
  const exportToExcel = () => {
    const selectedData = data.filter(item => selectedItems.includes(item.key));
    
    if (selectedData.length === 0) {
      Modal.warning({
        title: 'Selección requerida',
        content: 'Por favor seleccione al menos un pedido para exportar.'
      });
      return;
    }
    
    // Prepare data for Excel by formatting and selecting relevant columns
    const exportData = selectedData.map(item => ({
      'Número de Guía': item.numero_guia || 'Sin guía',
      'Nombre del Producto': item.nombre_producto,
      'Variación ID': item.id_variacion || 'Sin variación',
      'Talla': item.talla || 'Sin talla',
      'Color': item.color || 'Sin color',
      'Fecha': new Date(item.fecha_estado).toLocaleDateString('es-CO'),
      'Estado del Envío': item.estado_envio,
      'Transportadora': item.transportadora,
      'Valor Proveedor': item.valor_proveedor,
      'Valor Total': item.valor_total,
      'Utilidad': item.valor_utilidad,
      'Destinatario': item.nombre_destinatario,
      'Teléfono': item.telefono_destinatario,
      'Dirección': item.direccion_destinatario
    }));
    
    // Create Excel worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Create Excel workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos');
    
    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, `pedidos_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const menu = (
    <Menu>
      <Menu.Item key="1" onClick={downloadCombinedPDF}>Descargar PDFs seleccionados</Menu.Item>
    </Menu>
  );

  return (
    <>
      <NavbarDropshipping title="Órdenes y Envíos Bodega" />
      <div className='sucursalPedidos-misPedidos'>
        <div className="sucursalPedidos-misPedidos-header">
          <div className="sucursalPedidos-misPedidos-actions">
            <Dropdown overlay={menu}>
              <Button type="primary">
                Acciones <DownOutlined />
              </Button>
            </Dropdown>
            <Button 
              className="excel-export-button" 
              onClick={exportToExcel}
              icon={<FileExcelOutlined />}
            >
              Exportar
            </Button>
          </div>
          
          <div className="custom-search-input">
            <input 
              type="text" 
              placeholder="Buscar por producto, guía, destinatario, teléfono..." 
              onChange={handleSearch}
              className="search-input"
            />
            <div className="search-icon">
              <SearchOutlined />
            </div>
          </div>
          
          {/* Custom Transportadora Dropdown */}
          <div className="custom-dropdown" ref={transportadoraRef}>
            <div 
              className="custom-dropdown-selector"
              onClick={() => setShowTransportadoraDropdown(!showTransportadoraDropdown)}
            >
              <span>{filters.transportadora || "Filtrar por Transportadora"}</span>
              <span className="dropdown-arrow"><DownOutlined /></span>
            </div>
            {showTransportadoraDropdown && (
              <div className="custom-dropdown-menu">
                <div 
                  className={`custom-dropdown-item ${filters.transportadora === '' ? 'selected' : ''}`}
                  onClick={() => {
                    handleFilterChange('transportadora', '');
                    setShowTransportadoraDropdown(false);
                  }}
                >
                  Todas
                </div>
                <div 
                  className={`custom-dropdown-item ${filters.transportadora === 'interrapidisimo' ? 'selected' : ''}`}
                  onClick={() => {
                    handleFilterChange('transportadora', 'interrapidisimo');
                    setShowTransportadoraDropdown(false);
                  }}
                >
                  Interrapidisimo
                </div>
                <div 
                  className={`custom-dropdown-item ${filters.transportadora === 'coordinadora' ? 'selected' : ''}`}
                  onClick={() => {
                    handleFilterChange('transportadora', 'coordinadora');
                    setShowTransportadoraDropdown(false);
                  }}
                >
                  Coordinadora
                </div>
                <div 
                  className={`custom-dropdown-item ${filters.transportadora === 'servientrega' ? 'selected' : ''}`}
                  onClick={() => {
                    handleFilterChange('transportadora', 'servientrega');
                    setShowTransportadoraDropdown(false);
                  }}
                >
                  Servientrega
                </div>
                <div 
                  className={`custom-dropdown-item ${filters.transportadora === 'envia' ? 'selected' : ''}`}
                  onClick={() => {
                    handleFilterChange('transportadora', 'envia');
                    setShowTransportadoraDropdown(false);
                  }}
                >
                  Envia
                </div>
              </div>
            )}
          </div>

          {/* Custom Estado de Envío Dropdown */}
          <div className="custom-dropdown" ref={estadoEnvioRef}>
            <div 
              className="custom-dropdown-selector"
              onClick={() => setShowEstadoEnvioDropdown(!showEstadoEnvioDropdown)}
            >
              <span>{filters.estadoEnvio || "Filtrar por Estado de Envío"}</span>
              <span className="dropdown-arrow"><DownOutlined /></span>
            </div>
            {showEstadoEnvioDropdown && (
              <div className="custom-dropdown-menu">
                <div 
                  className={`custom-dropdown-item ${filters.estadoEnvio === '' ? 'selected' : ''}`}
                  onClick={() => {
                    handleFilterChange('estadoEnvio', '');
                    setShowEstadoEnvioDropdown(false);
                  }}
                >
                  Todos
                </div>
                <div 
                  className={`custom-dropdown-item ${filters.estadoEnvio === 'creado' ? 'selected' : ''}`}
                  onClick={() => {
                    handleFilterChange('estadoEnvio', 'creado');
                    setShowEstadoEnvioDropdown(false);
                  }}
                >
                  Creado
                </div>
                <div 
                  className={`custom-dropdown-item ${filters.estadoEnvio === 'en_transito' ? 'selected' : ''}`}
                  onClick={() => {
                    handleFilterChange('estadoEnvio', 'en_transito');
                    setShowEstadoEnvioDropdown(false);
                  }}
                >
                  En tránsito
                </div>
                <div 
                  className={`custom-dropdown-item ${filters.estadoEnvio === 'entregado' ? 'selected' : ''}`}
                  onClick={() => {
                    handleFilterChange('estadoEnvio', 'entregado');
                    setShowEstadoEnvioDropdown(false);
                  }}
                >
                  Entregado
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contenedor principal que incluye la tabla y la paginación */}
        <div className="sucursalPedidos-misPedidos-tabla-wrapper">
          {/* Contenedor solo para la tabla con scroll horizontal */}
          <div className="sucursalPedidos-misPedidos-tabla sucursalPedidos-misPedidos-custom-table-container">
            {filteredData.length > 0 ? (
              <table className="sucursalPedidos-misPedidos-custom-table">
                <thead>
                  <tr>
                    <th className="checkbox-column"></th>
                    <th>{renderColumnHeader('numero_guia', 'Número de Guía')}</th>
                    <th>{renderColumnHeader('nombre_producto', 'Nombre del producto')}</th>
                    <th>Variación</th>
                    <th>{renderColumnHeader('fecha_estado', 'Fecha de estado')}</th>
                    <th>{renderColumnHeader('estado_envio', 'Estado del Envío')}</th>
                    <th>{renderColumnHeader('transportadora', 'Transportadora')}</th>
                    <th>{renderColumnHeader('valor_proveedor', 'Valor de Proveedor')}</th>
                    <th>{renderColumnHeader('valor_total', 'Valor Total')}</th>
                    <th>{renderColumnHeader('valor_utilidad', 'Utilidad')}</th>
                    <th>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(item => (
                    <tr key={item.key} className={item.selected ? 'selected-row' : ''}>
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={item.selected}
                          onChange={() => handleCheckboxChange(item.key)}
                        />
                      </td>
                      <td>{item.numero_guia ? item.numero_guia : 'Sin guía'}</td>
                      <td>{item.nombre_producto}</td>
                      <td>{renderVariacion(item)}</td>
                      <td>
                        {new Date(item.fecha_estado).toLocaleDateString('es-CO', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </td>
                      <td>{item.estado_envio}</td>
                      <td>{item.transportadora}</td>
                      <td>${item.valor_proveedor.toLocaleString()}</td>
                      <td>${item.valor_total.toLocaleString()}</td>
                      <td>${item.valor_utilidad.toLocaleString()}</td>
                      <td>
                        {item.pdf ? (
                          <a href={item.pdf} target="_blank" rel="noopener noreferrer">
                            Ver PDF
                          </a>
                        ) : 'No disponible'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data-message">
                <p>No hay datos disponibles</p>
              </div>
            )}
          </div>

          {/* Paginación fuera del contenedor de scroll */}
          {filteredData.length > 0 && (
            <div className="custom-pagination">
              <div className="pagination-info">
                Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, sortedData.length)} de {sortedData.length} registros
              </div>
              <div className="pagination-controls">
                {/* Mostrar botón "1" solo si no está ya visible en los botones centrales */}
                {currentPage > 2 && (
                  <button 
                    onClick={() => paginate(1)} 
                    className="pagination-button"
                  >
                    1
                  </button>
                )}
                
                {/* Mostrar botón anterior solo si no estamos en la primera página */}
                {currentPage > 1 && (
                  <button 
                    onClick={() => paginate(currentPage - 1)} 
                    className="pagination-button"
                  >
                    &lt;
                  </button>
                )}
                
                {/* Números de página */}
                <div className="pagination-pages">
                  {(() => {
                    // Show only current page and one page before/after
                    const pagesToShow = [];
                    
                    // Add previous page if it exists
                    if (currentPage > 1) {
                      pagesToShow.push(currentPage - 1);
                    }
                    
                    // Add current page
                    pagesToShow.push(currentPage);
                    
                    // Add next page if it exists
                    if (currentPage < totalPages) {
                      pagesToShow.push(currentPage + 1);
                    }
                    
                    return pagesToShow.map(pageNum => (
                      <button 
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`pagination-button ${currentPage === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    ));
                  })()}
                </div>
                
                {/* Mostrar botón siguiente solo si no estamos en la última página */}
                {currentPage < totalPages && (
                  <button 
                    onClick={() => paginate(currentPage + 1)} 
                    className="pagination-button"
                  >
                    &gt;
                  </button>
                )}
                
                {/* Mostrar botón de última página solo si no está ya visible en los botones centrales */}
                {currentPage < totalPages - 1 && (
                  <button 
                    onClick={() => paginate(totalPages)} 
                    className="pagination-button"
                  >
                    {totalPages}
                  </button>
                )}
              </div>
              <div className="items-per-page">
                <span>Registros por página: </span>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductTablePedidos;
